import { isLeafKind, type MenuKind, type MenuNode } from '@/types/menu';

/**
 * The built-in menu — today's sidebar as data. Used ONLY when GET /api/menu
 * fails: before migration 131 has run, or when the Function App / SQL is
 * unreachable. It is the same menu migration 131 seeds, row for row —
 * fallback.test.ts parses the seed out of the migration and fails the build if
 * the two disagree.
 *
 * Once 131 has run, the LIVE menu is whatever an admin makes it at
 * /settings/menu; this file does not follow those edits. It is the
 * "the database is down, but you can still get around" menu.
 */

export interface SeedRow {
  key: string;
  parent: string | null;
  kind: MenuKind;
  title: string;
  icon: string | null;
  url: string | null;
  sort: number;
  roles: string[];
}

const READ = ['oscar-admin', 'oscar-helpdesk', 'oscar-finance', 'oscar-reviewer'];
const MUTATE = ['oscar-admin', 'oscar-helpdesk'];
const ADMIN = ['oscar-admin'];

// KEEP IN STEP with the MENU SEED block of
// api/migrations/OnsiteCore-131-menu-config.sql (fallback.test.ts enforces it).
export const SEED_ROWS: SeedRow[] = [
  { key: 'assets', parent: null, kind: 'Route', title: 'Assets', icon: 'laptop', url: '/assets', sort: 1, roles: READ },
  { key: 'azure-apps', parent: null, kind: 'Route', title: 'Azure Apps', icon: 'key-round', url: '/accounts', sort: 2, roles: READ },
  // Helpdesk reads Branch relationships but cannot see Admin; an admin reaches it under Admin.
  { key: 'branch-rel-helpdesk', parent: null, kind: 'Route', title: 'Branch relationships', icon: 'git-branch', url: '/settings/branch-relationships', sort: 3, roles: ['oscar-helpdesk'] },
  { key: 'contracts', parent: null, kind: 'Route', title: 'Contracts', icon: 'file-text', url: '/contracts', sort: 4, roles: READ },
  { key: 'dynamic-rules', parent: null, kind: 'Route', title: 'Dynamic rule search', icon: 'search', url: '/dynamic-rules', sort: 5, roles: MUTATE },
  // ⛔ Hardware requests carries delivery addresses and possibly personal numbers:
  // admin + helpdesk only. Widening it is a privacy decision, not a tidy-up.
  { key: 'hardware-requests', parent: null, kind: 'Route', title: 'Hardware requests', icon: 'clipboard-list', url: '/hardware-requests', sort: 6, roles: MUTATE },
  { key: 'locations', parent: null, kind: 'Route', title: 'Locations', icon: 'map-pin', url: '/locations', sort: 7, roles: READ },
  { key: 'offboarding', parent: null, kind: 'Route', title: 'Offboarding', icon: 'log-out', url: '/offboarding', sort: 8, roles: READ },
  { key: 'people', parent: null, kind: 'Menu', title: 'People / Accounts', icon: 'users', url: null, sort: 9, roles: READ },
  { key: 'people-register', parent: 'people', kind: 'Route', title: 'People / Accounts', icon: 'users', url: '/people', sort: 1, roles: READ },
  { key: 'new-starters', parent: 'people', kind: 'Route', title: 'New starters', icon: 'user-plus', url: '/onboarding/new-starters', sort: 2, roles: MUTATE },
  { key: 'services', parent: null, kind: 'Menu', title: 'Services', icon: 'network', url: null, sort: 10, roles: READ },
  { key: 'services-register', parent: 'services', kind: 'Route', title: 'Services', icon: 'network', url: '/services', sort: 1, roles: READ },
  { key: 'tier-map', parent: 'services', kind: 'Route', title: 'Tier map', icon: 'layers', url: '/services/tiers', sort: 2, roles: READ },
  { key: 'software', parent: null, kind: 'Route', title: 'Software', icon: 'app-window', url: '/software', sort: 11, roles: READ },
  { key: 'stock', parent: null, kind: 'Route', title: 'Stock', icon: 'package', url: '/stock', sort: 12, roles: READ },
  { key: 'quote-requests', parent: null, kind: 'Route', title: 'Quote requests', icon: 'file-text', url: '/stock/quotes', sort: 13, roles: READ },
  { key: 'access-reviews', parent: null, kind: 'Route', title: 'System Access Reviews', icon: 'clipboard-check', url: '/access-reviews', sort: 14, roles: READ },
  { key: 'onpass', parent: null, kind: 'Route', title: 'OnPass', icon: 'send', url: '/onpass', sort: 15, roles: ['oscar-admin', 'oscar-helpdesk', 'onpass-user'] },
  { key: 'finance-export', parent: null, kind: 'Route', title: 'Finance export', icon: 'download', url: '/finance/export', sort: 16, roles: ['oscar-admin', 'oscar-finance'] },
  { key: 'admin', parent: null, kind: 'Menu', title: 'Admin', icon: 'shield-check', url: null, sort: 17, roles: ADMIN },
  { key: 'admin-home', parent: 'admin', kind: 'Route', title: 'Admin', icon: 'shield-check', url: '/admin', sort: 1, roles: ADMIN },
  { key: 'settings', parent: 'admin', kind: 'Route', title: 'Settings', icon: 'settings', url: '/settings', sort: 2, roles: ADMIN },
  { key: 'branch-rel-admin', parent: 'admin', kind: 'Route', title: 'Branch relationships', icon: 'git-branch', url: '/settings/branch-relationships', sort: 3, roles: ADMIN },
  { key: 'contract-gl-defaults', parent: 'admin', kind: 'Route', title: 'Contract GL defaults', icon: 'file-text', url: '/contracts/gl-defaults', sort: 4, roles: ADMIN },
  { key: 'hardware-catalogue', parent: 'admin', kind: 'Route', title: 'Hardware catalogue', icon: 'laptop', url: '/hardware-catalogue', sort: 5, roles: ADMIN },
  { key: 'onboarding', parent: 'admin', kind: 'Submenu', title: 'Onboarding', icon: 'clipboard-list', url: null, sort: 6, roles: ADMIN },
  { key: 'department-codes', parent: 'onboarding', kind: 'Route', title: 'Department Codes', icon: 'building', url: '/onboarding/departments', sort: 1, roles: ADMIN },
  { key: 'position-titles', parent: 'onboarding', kind: 'Route', title: 'Position Title Allocator', icon: 'move', url: '/onboarding/position-titles', sort: 2, roles: ADMIN },
  { key: 'software-allocation', parent: 'onboarding', kind: 'Route', title: 'Software Allocation', icon: 'app-window', url: '/onboarding/software', sort: 3, roles: ADMIN },
  { key: 'hardware-allocation', parent: 'onboarding', kind: 'Route', title: 'Hardware Allocation', icon: 'laptop', url: '/onboarding/hardware', sort: 4, roles: ADMIN },
];

/**
 * The same visibility rule as `MenuFunctions.BuildVisibleTree` on the server:
 * no roles = everyone; otherwise hold one of them; a category needs a visible
 * child; oscar-admin does NOT bypass. Synthetic ids are negative so they can
 * never collide with a database id.
 */
export function buildFallbackTree(userRoles: string[], rows: SeedRow[] = SEED_ROWS): MenuNode[] {
  const held = new Set(userRoles.map((r) => r.toLowerCase()));
  const passes = (row: SeedRow) => row.roles.length === 0 || row.roles.some((r) => held.has(r.toLowerCase()));
  const ids = new Map(rows.map((r, i) => [r.key, -(i + 1)]));

  const build = (parent: string | null): MenuNode[] =>
    rows
      .filter((r) => r.parent === parent)
      .sort((a, b) => a.sort - b.sort || a.title.localeCompare(b.title))
      .flatMap((r): MenuNode[] => {
        if (!passes(r)) return [];
        const children = isLeafKind(r.kind) ? [] : build(r.key);
        if (!isLeafKind(r.kind) && children.length === 0) return [];
        return [{
          MenuItemId: ids.get(r.key)!,
          Kind: r.kind,
          Title: r.title,
          Description: null,
          Icon: r.icon,
          Url: r.url,
          Target: '_self',
          Children: children,
        }];
      });

  return build(null);
}
